"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.queryQuantumVariantsCounts = queryQuantumVariantsCounts;
exports.queryQuantumMetrics = exports.createQuantumApolloClient = void 0;

var _client = require("@apollo/client");

var _commaSeparateNumber = _interopRequireDefault(require("../utilities/commaSeparateNumber"));

var _getQuantumExperimentMetrics = require("./queries/getQuantumExperimentMetrics");

var _dayjs = _interopRequireDefault(require("dayjs"));

var _customParseFormat = _interopRequireDefault(require("dayjs/plugin/customParseFormat"));

var _getQuantumVariantCounts = require("./queries/getQuantumVariantCounts");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_dayjs.default.extend(_customParseFormat.default);

const {
  REACT_APP_QUANTUM_METRICS_API_URL
} = process.env;
const QUANTUM_DATE_FORMAT = 'YYYY-MM-DD';
const metricTypes = {
  1: 'Success',
  2: 'Safety'
};

const createQuantumApolloClient = ({
  quantumToken,
  fetch,
  quantumUrl = REACT_APP_QUANTUM_METRICS_API_URL
}) => {
  const link = new _client.HttpLink({
    uri: `${quantumUrl}`,
    fetchOptions: {
      mode: 'cors'
    },
    fetch,
    headers: {
      authorization: `Bearer ${quantumToken}`
    }
  });
  const cache = new _client.InMemoryCache();
  return new _client.ApolloClient({
    link,
    cache
  });
};

exports.createQuantumApolloClient = createQuantumApolloClient;

const calculateBonferriValue = (numOfVariants, alpha = 0.05) => {
  return alpha / (numOfVariants * (numOfVariants + 1) / 2);
};

const formatMeanEffect = meanEffect => `${(meanEffect * 100).toFixed(1) === '-0.0' ? '0.0' : (meanEffect * 100).toFixed(1)}%`;

const formatDisplayedMetric = ({
  meanValue,
  metricName,
  asOfDenverDate,
  showPercentage = false
}) => {
  if (metricName === 'Buy Flow Conversion Rate') {
    meanValue = meanValue * 100;
  }

  const isMos = metricName.toLowerCase().indexOf('mos') != -1; // older Quantum experiments did not return MOS as a percentage between 0-1

  if (isMos && meanValue > 1 && asOfDenverDate && (0, _dayjs.default)(asOfDenverDate, QUANTUM_DATE_FORMAT).isBefore((0, _dayjs.default)('2020-01-02', QUANTUM_DATE_FORMAT))) {
    meanValue = meanValue / 4.5957;

    if (!isNaN(meanValue)) {
      meanValue = meanValue.toFixed(3);
    }
  } // mos always shows the percentage


  if (isMos) showPercentage = true;

  if (showPercentage && meanValue != undefined && meanValue != null) {
    meanValue = meanValue * 100.0;
  }

  const split = meanValue.toString().split('.');
  let beforeDecimalLength = split[0].length;

  if (!Number(split[0])) {
    beforeDecimalLength = 0;
  } else if (meanValue < 0) {
    beforeDecimalLength = split[0].length - 1;
  }

  const afterDecimalLength = split[1] ? split[1].length : 0;
  let decimals = beforeDecimalLength <= 5 ? 5 - beforeDecimalLength : 0;
  decimals = showPercentage ? 1 : Math.min(decimals, afterDecimalLength);
  let displayedValue = meanValue.toFixed(decimals);

  if (Number(beforeDecimalLength) >= 1000) {
    displayedValue = (0, _commaSeparateNumber.default)(meanValue.toFixed(decimals));
  } else if (meanValue.toFixed(decimals) === '-0.0') {
    displayedValue = '0.0';
  }

  return showPercentage ? `${displayedValue.toLocaleString()}%` : `${displayedValue.toLocaleString()}`;
};

const createMetricResult = params => {
  const {
    denverDate: asOfDenverDate,
    meanA,
    meanB,
    differenceInMeans,
    meanEffect,
    pValue,
    numOfVariants,
    meanEffectConfidenceInterval,
    meanDifferenceConfidenceInterval,
    metricUnitAbbrev,
    metricDefinitionId,
    description,
    calculation,
    metricName,
    metricTypeName,
    metricImpactDirectionName,
    subdimensionName,
    subdimensionValue,
    lowerConfidenceInterval,
    upperConfidenceInterval
  } = params;
  const isSignificant = pValue !== null && pValue !== undefined && pValue <= calculateBonferriValue(numOfVariants - 1);
  let showRed = meanEffect > 0 ? metricImpactDirectionName.toLowerCase() === 'negative' : metricImpactDirectionName.toLowerCase() === 'positive';
  showRed = showRed && isSignificant;
  const displayedPValue = pValue.toFixed(3) < 0.001 ? '<0.001' : pValue.toFixed(3);

  const formatCI = ({
    lower,
    upper,
    showPercentage
  }) => {
    const answer = {
      lower: formatDisplayedMetric({
        meanValue: Number(lower),
        asOfDenverDate,
        metricName,
        showPercentage
      }),
      upper: formatDisplayedMetric({
        meanValue: Number(upper),
        asOfDenverDate,
        metricName,
        showPercentage
      })
    };
    return answer;
  };

  const showPercentage = metricUnitAbbrev === '%';
  const finalReturn = {
    metricTypeName,
    name: `${metricName} (${metricUnitAbbrev})`,
    description,
    calculation,
    uuid: metricDefinitionId,
    isSignificant,
    showRed,
    meanEffect: formatMeanEffect(meanEffect),
    differenceInMeans: formatDisplayedMetric({
      metricDefinitionId,
      asOfDenverDate,
      meanValue: differenceInMeans,
      metricName,
      showPercentage
    }),
    metricImpactDirectionName: metricImpactDirectionName.toLowerCase(),
    variantAMean: formatDisplayedMetric({
      asOfDenverDate,
      meanValue: meanA,
      metricName,
      showPercentage
    }),
    variantBMean: formatDisplayedMetric({
      asOfDenverDate,
      meanValue: meanB,
      metricName,
      showPercentage
    }),
    pValue: displayedPValue,
    meanEffectConfidenceInterval: meanEffectConfidenceInterval ? {
      lower: formatMeanEffect(meanEffectConfidenceInterval.lower),
      upper: formatMeanEffect(meanEffectConfidenceInterval.upper)
    } : null,
    meanDifferenceConfidenceInterval: meanDifferenceConfidenceInterval ? formatCI(meanDifferenceConfidenceInterval, true) : null,
    submetricName: subdimensionName,
    submetricValue: subdimensionValue,
    lowerConfidenceInterval: formatMeanEffect(lowerConfidenceInterval),
    upperConfidenceInterval: formatMeanEffect(upperConfidenceInterval)
  };
  return finalReturn;
};

const queryQuantumMetrics = async ({
  quantumToken,
  asOfDenverDate,
  metricDefinitions,
  experimentMetrics,
  quantumApplicationName,
  quantumApplicationType,
  experimentId,
  variantAUuid,
  variantBUuid,
  numOfVariants,
  mockData,
  observationStartDate = '',
  searchBackForResults = true,
  numDaysBackToSearch = 2,
  fetch,
  quantumUrl
}) => {
  let quantumMetricResults = null;
  let quantumMetricDefinitions = null;
  let date = (0, _dayjs.default)(asOfDenverDate, QUANTUM_DATE_FORMAT).format(QUANTUM_DATE_FORMAT);

  const hasAllMetricResults = () => {
    return quantumMetricResults && quantumMetricResults.length;
  };

  if (mockData) {
    quantumMetricResults = mockData.comparisonMetrics;
    quantumMetricDefinitions = mockData.metricDefinitions;
  } else {
    const quantumMetricsApiClient = createQuantumApolloClient({
      quantumToken,
      fetch,
      quantumUrl
    }); // This is a weird name and it comes from Quantum
    // and it designates essentially a Distillery product filter,
    // or applicationId

    const applicationCoords = quantumApplicationName && quantumApplicationType ? `${quantumApplicationName}@${quantumApplicationType}` : null;

    const searchForQuantumResults = async asOfDenverDate => {
      const variables = {
        applicationCoords,
        asOfDenverDate,
        experimentId: String(experimentId),
        variantAId: variantAUuid,
        variantBId: variantBUuid
      };

      if (observationStartDate.length) {
        variables.observationStartDate = observationStartDate;
        variables.metricIds = experimentMetrics.map(({
          metricUuid
        }) => metricUuid);
      }

      const {
        data
      } = await quantumMetricsApiClient.query({
        query: _getQuantumExperimentMetrics.GET_QUANTUM_EXPERIMENT_METRICS,
        variables
      });
      quantumMetricResults = data.comparisonMetrics;
      quantumMetricDefinitions = data.metricDefinitions;
      if (quantumMetricResults.length === 0) return false;
    };

    if (searchBackForResults) {
      // Search back a few days in a loop
      let daysBack = 0;

      while (!hasAllMetricResults() && daysBack < numDaysBackToSearch) {
        date = (0, _dayjs.default)(asOfDenverDate, QUANTUM_DATE_FORMAT).subtract(daysBack, 'day').format(QUANTUM_DATE_FORMAT);
        await searchForQuantumResults(date);
        daysBack++;
      }
    } else {
      // Search just for the date supplied
      await searchForQuantumResults(asOfDenverDate);
    }
  }

  if (!hasAllMetricResults()) throw Error(`No results found in Quantum for requested date of ${asOfDenverDate}`);
  const metricComparison = {
    date,
    variantAUuid,
    variantBUuid,
    metrics: []
  }; // Verify if there are no experiment metrics or they dont exist && metrics are avaliable for experiment

  if ((experimentMetrics && experimentMetrics.length === 0 || !experimentMetrics) && metricDefinitions) {
    metricDefinitions.forEach(({
      uuid: metricDefinitionId,
      displayName: metricName,
      metricType: {
        name: metricTypeName
      },
      metricImpactDirectionType: {
        name: metricImpactDirectionName
      }
    }) => {
      const quantumMetricResult = quantumMetricResults.find(({
        metricId,
        subdimensionName
      }) => metricId === metricDefinitionId && subdimensionName === null);
      const quantumMetricDef = quantumMetricDefinitions.find(({
        _id: metricId
      }) => metricId === metricDefinitionId);
      const {
        description = '',
        calculation = '',
        unit: {
          name: metricUnitName,
          abbreviation: metricUnitAbbrev
        }
      } = quantumMetricDef || {
        unit: {}
      };
      let metricRes;

      if (quantumMetricResult) {
        metricRes = createMetricResult({ ...quantumMetricResult,
          metricName,
          metricDefinitionId,
          description,
          calculation,
          metricUnitName,
          metricUnitAbbrev,
          metricTypeName,
          numOfVariants,
          metricImpactDirectionName
        });
        metricRes.submetrics = {};
        const submetrics = quantumMetricResults.filter(({
          metricId,
          subdimensionName
        }) => subdimensionName && metricId === metricDefinitionId);
        const subdimensions = []; // Loop through all submetrics and grab subdimensions

        submetrics.forEach(({
          subdimensionName
        }) => {
          if (subdimensionName && !subdimensions.includes(subdimensionName)) {
            subdimensions.push(subdimensionName);
          }
        });
        subdimensions.forEach(subdimensionName => {
          const submetricObjects = submetrics.filter(({
            subdimensionName: submetricName
          }) => subdimensionName === submetricName);
          const submetricResults = submetricObjects.map(submetric => {
            const submetricRes = createMetricResult({ ...submetric,
              metricName,
              metricDefinitionId,
              description,
              metricUnitName,
              metricUnitAbbrev,
              metricTypeName,
              numOfVariants,
              metricImpactDirectionName
            });
            return submetricRes;
          });
          metricRes.submetrics[subdimensionName] = submetricResults;
        });
      } else {
        metricRes = {
          metricTypeName,
          name: `${metricName}`,
          description,
          calculation,
          uuid: metricDefinitionId,
          isSignificant: false,
          showRed: false,
          meanEffect: 'N/A',
          differenceInMeans: 'N/A',
          metricImpactDirectionName: metricImpactDirectionName.toLowerCase(),
          variantAMean: 'N/A',
          variantBMean: 'N/A',
          pValue: 'N/A',
          lowerConfidenceInterval: 'N/A',
          upperConfidenceInterval: 'N/A',
          meanEffectConfidenceInterval: {
            lower: 'N/A',
            upper: 'N/A'
          },
          meanDifferenceConfidenceInterval: {
            lower: 'N/A',
            upper: 'N/A'
          }
        };
      }

      metricComparison.metrics.push(metricRes);
    });
  } else {
    experimentMetrics.forEach(({
      metricUuid: metricDefinitionId,
      metricTypeId,
      observationalDays
    }) => {
      const quantumMetricResult = quantumMetricResults.find(({
        metricId,
        subdimensionName
      }) => metricId === metricDefinitionId && subdimensionName === null);
      const metricInfo = quantumMetricDefinitions.find(({
        _id: metricId
      }) => metricId === metricDefinitionId);
      const {
        displayName: metricName,
        description,
        calculation,
        direction: metricImpactDirectionName,
        unit: {
          name: metricUnitName,
          abbreviation: metricUnitAbbrev
        }
      } = metricInfo;
      let metricRes;
      const metricTypeName = metricTypes[metricTypeId];

      if (quantumMetricResult) {
        metricRes = createMetricResult({ ...quantumMetricResult,
          metricName,
          metricUnitName,
          metricUnitAbbrev,
          metricTypeName,
          description,
          calculation,
          metricImpactDirectionName,
          metricDefinitionId,
          numOfVariants,
          observationalDays
        });
        metricRes.submetrics = {};
        const submetrics = quantumMetricResults.filter(({
          metricId,
          subdimensionName
        }) => subdimensionName && metricId === metricDefinitionId);
        const subdimensions = []; // Loop through all submetrics and grab subdimensions

        submetrics.forEach(({
          subdimensionName
        }) => {
          if (subdimensionName && !subdimensions.includes(subdimensionName)) {
            subdimensions.push(subdimensionName);
          }
        });
        subdimensions.forEach(subdimensionName => {
          const submetricObjects = submetrics.filter(({
            subdimensionName: submetricName
          }) => subdimensionName === submetricName);
          const submetricResults = submetricObjects.map(submetric => {
            const submetricRes = createMetricResult({ ...submetric,
              metricName,
              metricDefinitionId,
              description,
              metricUnitName,
              metricUnitAbbrev,
              metricTypeName,
              numOfVariants,
              metricImpactDirectionName
            });
            return submetricRes;
          });
          metricRes.submetrics[subdimensionName] = submetricResults;
        });
      } else {
        metricRes = {
          metricTypeName,
          name: `${metricName}`,
          description,
          calculation,
          uuid: metricDefinitionId,
          isSignificant: false,
          showRed: false,
          meanEffect: 'N/A',
          differenceInMeans: 'N/A',
          metricImpactDirectionName: metricImpactDirectionName.toLowerCase(),
          variantAMean: 'N/A',
          variantBMean: 'N/A',
          pValue: 'N/A',
          meanEffectConfidenceInterval: {
            lower: 'N/A',
            upper: 'N/A'
          },
          meanDifferenceConfidenceInterval: {
            lower: 'N/A',
            upper: 'N/A'
          },
          observationalDays
        };
      }

      metricComparison.metrics.push(metricRes);
    });
  }

  return metricComparison;
};

exports.queryQuantumMetrics = queryQuantumMetrics;

async function queryQuantumVariantsCounts({
  quantumToken,
  fetch,
  experimentUuid,
  asOfDenverDate,
  quantumUrl,
  quantumApplicationName,
  quantumApplicationType,
  subdimensions = [null]
}) {
  const quantumClient = await createQuantumApolloClient({
    quantumToken,
    fetch,
    quantumUrl
  });
  const applicationCoords = quantumApplicationName && quantumApplicationType ? `${quantumApplicationName}@${quantumApplicationType}` : null;
  const res = quantumClient.query({
    query: _getQuantumVariantCounts.GET_QUANTUM_VARIANT_COUNTS,
    variables: {
      experimentId: experimentUuid,
      asOfDenverDate,
      applicationCoords,
      subdimensions
    }
  });
  return res;
}